import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  CloseIcon,
  Field,
  FormLabel,
  Heading,
  Input,
  LoadingSection,
  Paragraph,
  SearchIcon,
  Switch,
  Tabset,
} from '@quickmd-llc/design-system'
import { PageLayout } from 'components/page-layouts/PageLayout'
import { DEFAULT_SORT, SORT_BY_ENUM, type SortKey, type SortState } from './constants'
import { formatCost } from './utils'
import { usePharmacyOrders, type PharmacyOrderQuery } from './hooks/usePharmacyOrders'
import { OrdersTable } from './components/OrdersTable'

const SEARCH_DEBOUNCE_MS = 300
// Columns that default to descending when first sorted (most-recent / highest first).
const DESC_FIRST_COLUMNS: SortKey[] = ['orderCreated', 'rxCreated', 'cost']

type OrdersView = 'tracking' | 'printQueue'

export function PharmacyOrdersPage() {
  const [view, setView] = useState<OrdersView>('tracking')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [notSentToScriptly, setNotSentToScriptly] = useState(false)

  // Debounce search input into the query value so typing doesn't refetch per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const params = useMemo<PharmacyOrderQuery>(
    // @ts-expect-error TS2375 -- MIGRATION: not approved, fix when encountered
    () => ({
      SearchValue: search || undefined,
      SortBy: SORT_BY_ENUM[sort.key][sort.direction],
      ShipmentStatuses: statusFilter.length ? statusFilter : undefined,
      IsPrintQueue: view === 'printQueue' ? true : undefined,
      CanTriggerFulfillment: notSentToScriptly ? true : undefined,
    }),
    [search, sort, statusFilter, view, notSentToScriptly],
  )

  const {
    items,
    totalOrderCount,
    totalShippingCost,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = usePharmacyOrders(params)

  const displayedItems = items

  const anyFilter = !!search || statusFilter.length > 0 || notSentToScriptly

  function handleSort(key: SortKey) {
    setSort((prev) =>
      prev.key === key ?
        { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: DESC_FIRST_COLUMNS.includes(key) ? 'desc' : 'asc' },
    )
  }

  function handleClearFilters() {
    setSearchInput('')
    setSearch('')
    setStatusFilter([])
    setNotSentToScriptly(false)
  }

  return (
    <PageLayout>
      {/* Tailwind is scoped under `.tailwind-scoped` in provider — the whole tree must
          live inside this wrapper for tokens/utilities to apply. */}
      <div className='tailwind-scoped' style={{ height: '100%' }}>
        <div className='grid h-full gap-sp-2 [grid-template-rows:auto_auto_auto_1fr]'>
          <div>
            <Heading level='h1'>Tracking</Heading>
            {!isLoading && !isError && view === 'tracking' ?
              <Paragraph size='sm' className='text-text-light'>
                {totalOrderCount} {totalOrderCount === 1 ? 'order' : 'orders'} · {formatCost(totalShippingCost)}{' '}
                shipping
              </Paragraph>
            : null}
          </div>

          <Tabset
            activeTab={view}
            onTabChange={(next) => setView(next === 'printQueue' ? 'printQueue' : 'tracking')}
            tabs={[
              { label: 'Tracking', value: 'tracking' },
              { label: 'Print queue', value: 'printQueue' },
            ]}
          />

          <div className='flex items-center gap-sp-2'>
            <div className='w-[260px]'>
              <Input
                placeholder='Search by MRN or patient'
                leftSideLabel={<SearchIcon iconSize='s' color='text-text-light' />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Field className='flex items-center gap-sp-1'>
              <Switch size='s' checked={notSentToScriptly} onChange={setNotSentToScriptly} />
              <FormLabel size='sm'>Not sent to Scriptly</FormLabel>
            </Field>
            {anyFilter ?
              <Button priority='tertiary' size='s' onClick={handleClearFilters}>
                <CloseIcon iconSize='s' />
                Clear filters
              </Button>
            : null}
          </div>

          <div className='flex min-h-0 flex-col overflow-hidden rounded-large border border-border-medium bg-white'>
            <OrdersPanel
              isLoading={isLoading}
              isError={isError}
              items={displayedItems}
              emptyMessage={view === 'printQueue' ? 'No results' : 'No orders match your filters.'}
              sort={sort}
              onSort={handleSort}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              showScriptlyColumn={notSentToScriptly}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={fetchNextPage}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

type OrdersPanelProps = {
  isLoading: boolean
  isError: boolean
  items: ReturnType<typeof usePharmacyOrders>['items']
  emptyMessage: string
  sort: SortState
  onSort: (key: SortKey) => void
  statusFilter: string[]
  onStatusFilterChange: (next: string[]) => void
  showScriptlyColumn: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}

function OrdersPanel({
  isLoading,
  isError,
  items,
  emptyMessage,
  sort,
  onSort,
  statusFilter,
  onStatusFilterChange,
  showScriptlyColumn,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: OrdersPanelProps) {
  if (isLoading) {
    return <LoadingSection />
  }

  if (isError) {
    return (
      <div className='flex h-full items-center justify-center p-sp-4'>
        <Paragraph size='sm' className='text-center text-text-light'>
          Something went wrong loading orders. Please try again.
        </Paragraph>
      </div>
    )
  }

  return (
    <div className='flex min-h-0 flex-col'>
      <div className='min-h-0 flex-1 overflow-auto'>
        {items.length === 0 ?
          <div className='flex h-full items-center justify-center p-sp-4'>
            <Paragraph size='sm' className='text-center text-text-light'>
              {emptyMessage}
            </Paragraph>
          </div>
        : <OrdersTable
            items={items}
            sort={sort}
            onSort={onSort}
            statusFilter={statusFilter}
            onStatusFilterChange={onStatusFilterChange}
            showScriptlyColumn={showScriptlyColumn}
          />
        }
      </div>
      {hasNextPage ?
        <div className='flex justify-center border-t border-border-light p-sp-1'>
          <Button priority='tertiary' size='s' loading={isFetchingNextPage} onClick={onLoadMore}>
            Load more
          </Button>
        </div>
      : null}
    </div>
  )
}
